import {Component, OnDestroy, OnInit} from '@angular/core';
import {PlatformQuery} from '@shared/models/platform-query/platform-query';
import {switchMap, take} from 'rxjs/operators';
import {Api} from '@shared/constants/common-field.constants';
import {PlatformQueryService} from '@core/services/platform-query.service';
import {IDialogView} from '@shared/components/ok-cancel-dialog/ok-cancel-dialog.component';
import {DialogModel} from '@shared/models/dialog.model';
import {DataMappingItem} from '@features/data-mapping/models/data-mapping.model';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {DialogService} from '@shared/services/dialog-service';
import {OptionItem} from '@shared/models/option-item';
import {PlatformCommand} from '@shared/commands/command-base';
import {CommandService} from '@core/services/command.service';
import {EMPTY} from 'rxjs';
import {BasketProvider} from '@shared/models/basket-provider';
import {ChangeBasketAction} from '@shared/store/actions/particle.actions';
import {IQueryState} from '@shared/store/reducers/query.reducer';
import {Store} from '@ngrx/store';
import {Router} from '@angular/router';
import {FileSelectorModel} from '@features/file-manager/models/file-selector.model';
import {SnackBarService} from '@core/services/snack-bar.service';
import {IconViewModel} from '@features/file-manager/models/file-manager.model';

@Component({
    standalone: false,
    selector: 'app-select-data-mapping-dialog',
  templateUrl: './select-data-mapping-dialog.component.html',
  styleUrls: ['./select-data-mapping-dialog.component.scss']
})
export class SelectDataMappingDialogComponent implements OnInit, IDialogView, OnDestroy {
  data: DialogModel;
  dataMappings: DataMappingItem[];
  dataMappingOptions: OptionItem[];
  selectorForm: FormGroup;
  hasDataMappingOption: boolean;
  dataMappingUrl: string;
  noDataMappingFoundMessage: string;
  hasDataMappingItems: boolean;
  optionItemPlaceholder: string;

  constructor(private platformQueryService: PlatformQueryService,
              private fb: FormBuilder,
              protected store: Store<IQueryState>,
              private dialogService: DialogService,
              private commandService: CommandService,
              private router: Router,
              private snackBarService: SnackBarService) {
  }

  ngOnInit(): void {
    this.data.isDisabledOkButton = true;
    this.dataMappings = this.data.input.dataMappings;
    this.dataMappingUrl = this.data.input.dataMappingUrl;
    this.dataMappingOptions = this.data.input.optionItems;
    this.noDataMappingFoundMessage = this.data.input.noDataMappingFoundMessage;
    this.hasDataMappingOption = this.data.input.hasDataMappingOption ?? true;
    this.hasDataMappingItems = this.data.input.hasDataMappingItems;
    this.optionItemPlaceholder = this.data.input.optionItemPlaceholder;
    if (this.hasDataMappingItems) {
      this.selectorForm = this.fb.group({
        selectedMapping: ['', [Validators.required]],
        dataMappingOption: ['', [Validators.required]],
        sourceFilePath: ['']
      });
      if (this.dataMappings.length <= 0) {
        this.getDataMappings();
      }
      this.selectorForm.valueChanges.subscribe(() => {
        this.data.isDisabledOkButton = this.selectorForm.invalid;
      });
      this.selectorForm.get('selectedMapping').valueChanges.subscribe(res => {
        if (res && this.dataMappingOptions) {
          const name = res.name;
          const fs = this.dataMappingOptions.filter(o => o.tags.indexOf(name) !== -1) as OptionItem[];
          if (Boolean(fs.length)) {
            this.selectorForm.get('dataMappingOption').setValue(fs[0]);
          }
        }
      });
      this.selectorForm.get('dataMappingOption').valueChanges.subscribe(res => {
        if (res.id === 'create-new-questionnaire') {
          this.selectorForm.addControl('filename', this.fb.control('', [Validators.required]));
        } else {
          if (this.selectorForm.contains('filename')) {
            this.selectorForm.removeControl('filename');
          }
        }
      });
      if (!this.hasDataMappingOption) {
        this.selectorForm.removeControl('dataMappingOption');
      }
    }
  }

  onOkClicked(): boolean {
    if (this.selectorForm.valid) {
      this.data.input.selectedMapping = this.selectorForm.get('selectedMapping').value;
      this.data.input.sourceFilePath = this.selectorForm.get('sourceFilePath').value;
      if (this.hasDataMappingOption) {
        this.data.input.dataMappingOption = (this.selectorForm.get('dataMappingOption').value as OptionItem).id;
        if (this.selectorForm.get('dataMappingOption').value.id === 'create-new-questionnaire') {
          this.data.input.fileName = this.selectorForm.get('filename').value;
        }
      }
      if (this.data.input.selectedMapping && this.data.input.selectedMapping.fieldValues['SelectedSourceConfig']) {
        const basketId = this.data.input.selectedMapping.fieldValues['SelectedSourceConfig'].basketId;
        if (basketId) {
          this.switchBasket(basketId);
        }
      }
      return true;
    }
    return false;
  }

  onCancelClicked(): any {
  }

  ngOnDestroy(): void {

  }

  showSelectedMapping() {
    const fileName = this.data.input['DirectoryEntryName'];
    const title = fileName ? `Applicable Data Mapping for ${fileName}` : 'Applicable Data Mapping';
    const dataMappingItemId = (this.selectorForm.get('selectedMapping').value as DataMappingItem).id;
    this.openPlatformToolDialog(dataMappingItemId, title);
    // this.openApplicableDataMappingDialog(title, fileName, dataMappingItemId);
  }

  goToFieldMapping() {
    this.router.navigateByUrl(this.dataMappingUrl).then(() => {
      this.data.dialogRef.close();
    });
  }


  openFileSelector(title: string, hideFiles: boolean, foldersSelectable: boolean) {
    const fileSelectorModel = new FileSelectorModel();
    fileSelectorModel.parentId = 'Team Drives';
    fileSelectorModel.isClientDrive = true;
    fileSelectorModel.iconViewModels = [];
    fileSelectorModel.fileExtensionTypes = ['.qnr'];
    fileSelectorModel.hideFiles = hideFiles;
    fileSelectorModel.skipBreadcrumbItem = false;
    fileSelectorModel.returnType = 'RelativePath';
    fileSelectorModel.foldersSelectable = foldersSelectable;

    this.dialogService.showOrbitaxDriveFileSelector(fileSelectorModel, title).subscribe(state => {
      if (state && state.action === 'OK') {
        const {stringifyRelativePath, type} = state.data.input.selectedItems[0] as IconViewModel;
        switch (type) {
          case 'File':
            const sourceFilePath = this.selectorForm.get('sourceFilePath');
            sourceFilePath.setValue(stringifyRelativePath);
            break;
          default:
            this.snackBarService.showError('Please select a source file');
            break;
        }
      }
    });
  }

  private openApplicableDataMappingDialog(title: string, fileName, dataMappingItemId: string) {
    const dialogModel: DialogModel = DialogModel.create(title,
      'ApplyDataMappingDetailViewDialogComponent', {
        flowchartSectionId: this.data.input['FlowchartSectionId'],
        SourceType: this.data.input['SourceType'],
        FlowchartId: this.data.input['FlowchartId'],
        ItemId: this.data.input['ItemId'],
        DirectoryEntryId: this.data.input['DirectoryEntryId'],
        DirectoryEntryName: fileName,
        DataMappingItemId: dataMappingItemId
      },
      '865px',
      '865px'
    );
    dialogModel.isFullscreen = true;

    this.dialogService.showOkCancelDialog(dialogModel).subscribe(res => {
      if (res && res.action === 'OK') {
        return true;
      }
    });
  }

  private openPlatformToolDialog(dataMappingItemId: string, title: string) {
    const command = new PlatformCommand('DataMappingDialogPlatformLinkCommand', Api.FileStoreApi);
    command.setValue('DataMappingItemId', dataMappingItemId);
    this.commandService.executePlatformCommand(command).pipe(switchMap(res => {
      if (res && res.fieldValues) {
        return this.dialogService.showPlatformToolDialog(title, res.fieldValues['PlatformLinkModel']);
      } else {
        return EMPTY;
      }
    })).subscribe(console.log);
  }

  private getDataMappings() {
    const query = new PlatformQuery();
    query.name = 'GetDataMappingQuery';
    query.api = Api.FileStoreApi;
    query.setValue('FlowchartSectionId', this.data.input['flowchartSectionId']);
    query.setValue('DirectoryEntryId', this.data.input['DirectoryEntryId']);
    query.setValue('IsFilterApplicable', this.data.input['isFilterApplicable']);
    this.platformQueryService.executeQuery(query).pipe(take(1)).subscribe(response => {
      if (response && response.fieldValues) {
        console.log('[GetDataMappingQuery]', response.fieldValues);
        this.dataMappings = response.fieldValues['DataMappings'];
        this.mapWithSelectedData();
      }
    });
  }

  private mapWithSelectedData() {
    const {dataMappingItemId, selectedActionId, SourceFilePath} = this.data.input;
    if (dataMappingItemId) {
      const selectedDataMapping = this.dataMappings.find(item => item.id === dataMappingItemId) as DataMappingItem;
      if (selectedDataMapping) {
        this.selectorForm.get('selectedMapping').patchValue(selectedDataMapping, {onlySelf: true, emitEvent: false});
      }
    }
    if (selectedActionId) {
      const selectedAction = this.dataMappingOptions.find(item => item.id === selectedActionId) as OptionItem;
      if (selectedAction) {
        this.selectorForm.get('dataMappingOption').patchValue(selectedAction, {onlySelf: true, emitEvent: false});
      }
    }
    if (SourceFilePath) {
      this.selectorForm.get('sourceFilePath').patchValue(SourceFilePath, {onlySelf: true, emitEvent: false});
    }
  }

  private switchBasket(basketId: string) {
    const currentBasket = BasketProvider.getCurrentBasket();
    const basket = BasketProvider.getBaskets().find(o => o.id === basketId);
    if (basket) {
      console.log('Switching basket from questionnaire using basketId-', basket.id);
      this.store.dispatch(new ChangeBasketAction(basket));
    }
  }
}
